import { useEffect, useState } from 'react';
import { Check, FileText, HeartPulse, LockKeyhole, RefreshCw, UploadCloud, X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import type { IntakeDocType } from '@refdash/api-client';
import { api } from '@/lib/api';
import { findIntakeDocumentIssue, formatDocumentSize, type IntakeDocumentIssue } from '@/lib/intake-documents';
import { loadIntakeLang, saveIntakeLang, t, type IntakeStringKey, type Lang } from '@/lib/intake-i18n';

interface Branding {
  display_name: string;
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
}

interface FormInfo {
  hospital_id: string;
  hospital_name: string;
  hospital_name_ar?: string | null;
  patient_hint?: string | null;
  initial_data?: Record<string, unknown>;
  consent_version: string;
  consent_text: string;
  recaptcha_site_key?: string | null;
  branding: Branding;
}

type IdentityType = 'national_id' | 'iqama' | 'passport' | 'other';
type State =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string | null }
  | { kind: 'form'; info: FormInfo }
  | { kind: 'done'; reference: string; hospitalId: string; uploaded: number; uploadErrors: number };

const FORM_UNAVAILABLE_SENTINEL = '__form_unavailable__';
const SUBMIT_FAILED_SENTINEL = '__submit_failed__';
const RECAPTCHA_FAILED_SENTINEL = '__recaptcha_failed__';

type FormError =
  | { kind: 'docs'; issue: IntakeDocumentIssue }
  | { kind: 'key'; key: 'err_submit_failed' | 'err_recaptcha_load' }
  | { kind: 'server'; message: string };

const DOC_ISSUE_KEYS: Record<IntakeDocumentIssue['code'], IntakeStringKey> = {
  too_many: 'err_too_many_documents',
  unsupported_type: 'err_unsupported_type',
  too_large: 'err_too_large',
  empty: 'err_empty_file',
};

function formErrorText(lang: Lang, error: FormError): string {
  if (error.kind === 'server') return error.message;
  if (error.kind === 'key') return t(lang, error.key);
  const issue = error.issue;
  return t(lang, DOC_ISSUE_KEYS[issue.code], 'name' in issue ? { name: issue.name } : undefined);
}

interface DocSection {
  id: IntakeDocType;
  titleKey: IntakeStringKey;
  hintKey: IntakeStringKey;
  multiple: boolean;
}

const DOC_SECTIONS: DocSection[] = [
  { id: 'identity_document', titleKey: 'doc_identity_title', hintKey: 'doc_identity_hint', multiple: false },
  { id: 'medical_report', titleKey: 'doc_medical_title', hintKey: 'doc_medical_hint', multiple: true },
  { id: 'no_insurance_proof', titleKey: 'doc_no_insurance_title', hintKey: 'doc_no_insurance_hint', multiple: false },
  { id: 'other', titleKey: 'doc_other_title', hintKey: 'doc_other_hint', multiple: true },
];

type DocState = Record<IntakeDocType, File[]>;
const EMPTY_DOCS: DocState = { identity_document: [], medical_report: [], no_insurance_proof: [], other: [] };
const allDocuments = (docs: DocState): File[] => DOC_SECTIONS.flatMap((section) => docs[section.id]);

export function IntakeSubmitPage() {
  const { token } = useParams<{ token: string }>();
  return <IntakeLoader infoUrl={`/api/intake/submit/${token}`} submitUrl={`/api/intake/submit/${token}`} />;
}

export function PublicIntakePage() {
  const { hospitalId } = useParams<{ hospitalId: string }>();
  const encoded = encodeURIComponent(hospitalId ?? '');
  return <IntakeLoader infoUrl={`/api/intake/public/${encoded}`} submitUrl={`/api/intake/public/${encoded}`} />;
}

function IntakeLoader({ infoUrl, submitUrl }: { infoUrl: string; submitUrl: string }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [lang, setLang] = useState<Lang>(() => loadIntakeLang());
  const toggleLang = () => {
    const next: Lang = lang === 'ar' ? 'en' : 'ar';
    setLang(next);
    saveIntakeLang(next);
  };

  useEffect(() => {
    let alive = true;
    fetch(infoUrl)
      .then(async (response) => {
        if (!response.ok) {
          const detail = (await response.json().catch(() => ({}))).detail;
          throw new Error(typeof detail === 'string' && detail ? detail : FORM_UNAVAILABLE_SENTINEL);
        }
        return response.json();
      })
      .then((info: FormInfo) => { if (alive) setState({ kind: 'form', info }); })
      .catch((error: Error) => {
        if (alive) setState({ kind: 'invalid', message: error.message === FORM_UNAVAILABLE_SENTINEL ? null : error.message });
      });
    return () => { alive = false; };
  }, [infoUrl]);

  let content: React.ReactNode;
  if (state.kind === 'loading') content = <Centered><Spinner /></Centered>;
  else if (state.kind === 'invalid') {
    content = <Centered><MessageCard title={t(lang, 'form_unavailable_title')} message={state.message ?? t(lang, 'form_unavailable_message')} /></Centered>;
  } else if (state.kind === 'done') content = <Done lang={lang} {...state} />;
  else {
    content = (
      <PatientForm
        lang={lang}
        info={state.info}
        submitUrl={submitUrl}
        onDone={(reference, hospitalId, uploaded, uploadErrors) => setState({ kind: 'done', reference, hospitalId, uploaded, uploadErrors })}
      />
    );
  }

  return (
    <div dir={lang === 'ar' ? 'rtl' : 'ltr'} className="relative">
      {/* Parked at the END of the reading direction — opposite the header
          title, which starts at the leading edge. A physical `right-4` sat on
          top of the Arabic title, since RTL text starts on the right. */}
      <button
        type="button"
        onClick={toggleLang}
        className="absolute end-4 top-4 z-30 rounded-full border border-black/10 bg-white/95 px-3.5 py-1.5 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-white"
      >
        {t(lang, 'language_toggle')}
      </button>
      {content}
    </div>
  );
}

function PatientForm({ lang, info, submitUrl, onDone }: { lang: Lang; info: FormInfo; submitUrl: string; onDone: (ref: string, hid: string, uploaded: number, errors: number) => void }) {
  const initial = info.initial_data ?? {};
  const accent = info.branding.primary_color || info.branding.accent_color || '#272361';
  const isAr = lang === 'ar';
  const [patientName, setPatientName] = useState(String(initial.name ?? initial.patient_name ?? info.patient_hint ?? ''));
  const [identityType, setIdentityType] = useState<IdentityType>((initial.identity_type as IdentityType) ?? 'national_id');
  const [nationalId, setNationalId] = useState(String(initial.national_id ?? ''));
  const [phone, setPhone] = useState(String(initial.phone ?? ''));
  const [nationality, setNationality] = useState(String(initial.nationality ?? ''));
  const [cause, setCause] = useState(String(initial.referral_cause ?? ''));
  const [sourceProvider, setSourceProvider] = useState(String(initial.source_provider ?? ''));
  const [paymentMethod, setPaymentMethod] = useState<'' | 'cash' | 'insurance'>(
    initial.payment_method === 'cash' || initial.payment_method === 'insurance' ? initial.payment_method : '',
  );
  const [docs, setDocs] = useState<DocState>(EMPTY_DOCS);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<FormError | null>(null);

  const totalFiles = allDocuments(docs).length;
  const ready = patientName.trim().length > 1 && phone.trim().length > 4 && cause.trim().length > 1 && !!paymentMethod && consent && !submitting;
  const staffContext = [initial.required_specialty, initial.ref_type, initial.bed_type].filter(Boolean).map(String);
  const sourceProviderLocked = Boolean(initial.source_provider);

  const addDocuments = (section: DocSection, picked: File[]) => {
    if (picked.length === 0) return;
    const nextSection = section.multiple ? [...docs[section.id], ...picked] : picked.slice(0, 1);
    const next = { ...docs, [section.id]: nextSection };
    const issue = findIntakeDocumentIssue(allDocuments(next));
    if (issue) {
      setError({ kind: 'docs', issue });
      return;
    }
    setError(null);
    setDocs(next);
  };

  const removeDocument = (sectionId: IntakeDocType, index: number) => {
    setDocs((current) => ({ ...current, [sectionId]: current[sectionId].filter((_, i) => i !== index) }));
    setError((current) => (current?.kind === 'docs' ? null : current));
  };

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      const recaptchaToken = info.recaptcha_site_key ? await executeRecaptcha(info.recaptcha_site_key) : null;
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name: patientName,
          identity_type: identityType,
          national_id: nationalId || null,
          phone,
          nationality: nationality || null,
          referral_cause: cause,
          source_provider: sourceProvider || null,
          payment_method: paymentMethod || null,
          consent,
          recaptcha_token: recaptchaToken,
          website: '',
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(typeof body.detail === 'string' && body.detail ? body.detail : SUBMIT_FAILED_SENTINEL);
      let uploaded = 0; let uploadErrors = 0;
      for (const section of DOC_SECTIONS) {
        for (const file of docs[section.id]) {
          try {
            await api.intake.uploadPatientDocument(body.upload_token, file, section.id);
            uploaded += 1;
          } catch {
            uploadErrors += 1;
          }
        }
      }
      onDone(body.reference, body.hospital_id, uploaded, uploadErrors);
    } catch (caught) {
      const message = (caught as Error).message;
      if (message === SUBMIT_FAILED_SENTINEL) setError({ kind: 'key', key: 'err_submit_failed' });
      else if (message === RECAPTCHA_FAILED_SENTINEL) setError({ kind: 'key', key: 'err_recaptcha_load' });
      else setError({ kind: 'server', message });
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh bg-[#f4f2ed] text-slate-950">
      <header className="relative overflow-hidden px-5 py-7 text-white" style={{ backgroundColor: accent }}>
        <div className="absolute -right-16 -top-24 h-64 w-64 rounded-full border-[36px] border-white/5" />
        {/* pe-24 keeps a long hospital name clear of the floating language
            toggle on a phone, in either direction. */}
        <div className="relative mx-auto flex max-w-xl items-center gap-3 pe-24">
          {info.branding.logo_url ? <img src={info.branding.logo_url} alt="" className="h-11 w-11 rounded-xl bg-white p-1.5 object-contain" /> : <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/12"><HeartPulse className="h-5 w-5" /></div>}
          <div>
            <p className="text-lg font-black leading-tight">{info.branding.display_name}</p>
            {(() => {
              const hospitalLine = lang === 'ar'
                ? (info.hospital_name_ar || info.hospital_name)
                : info.hospital_name;
              // Under a group co-brand the specific hospital must still be
              // named; skip the line only when it would repeat the header.
              return (
                <p className="mt-0.5 text-xs font-semibold text-white/90">
                  {hospitalLine !== info.branding.display_name && <>{hospitalLine}
                    <span className="font-normal text-white/70"> · </span></>}
                  <span className="font-normal text-white/70">{t(lang, 'header_subtitle')}</span>
                </p>
              );
            })()}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-4 py-6 sm:px-5 sm:py-9">
        <div className="mb-5 flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-950">
          <HeartPulse className="mt-0.5 h-4 w-4 shrink-0" />
          <p><strong>{t(lang, 'emergency_strong')}</strong> {t(lang, 'emergency_rest')}</p>
        </div>

        <div className="overflow-hidden rounded-[1.35rem] border border-black/10 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.55)]">
          <div className="border-b border-black/10 px-5 py-5 sm:px-7">
            <p className="text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: accent }}>{t(lang, 'packet_kicker')}</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight">{t(lang, 'packet_title')}</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">{t(lang, 'packet_subtitle')}</p>
            {staffContext.length > 0 && <div className="mt-4 rounded-xl bg-slate-950 px-4 py-3 text-xs text-slate-200"><span className="font-bold text-white">{t(lang, 'prepared_by_staff')}</span> {staffContext.join(' · ')}</div>}
          </div>

          <div className="space-y-5 px-5 py-6 sm:px-7">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t(lang, 'full_name')} required><input value={patientName} onChange={(e) => setPatientName(e.target.value)} className="patient-input" autoComplete="name" /></Field>
              <Field label={t(lang, 'mobile')} required><input value={phone} onChange={(e) => setPhone(e.target.value)} className="patient-input" dir="ltr" inputMode="tel" autoComplete="tel" placeholder={t(lang, 'mobile_placeholder')} /></Field>
              <div className="grid grid-cols-[0.85fr_1.15fr] gap-2 sm:col-span-2">
                <Field label={t(lang, 'identity_type')}>
                  <select value={identityType} onChange={(e) => setIdentityType(e.target.value as IdentityType)} className="patient-input">
                    <option value="national_id">{t(lang, 'identity_type_national_id')}</option>
                    <option value="iqama">{t(lang, 'identity_type_iqama')}</option>
                    <option value="passport">{t(lang, 'identity_type_passport')}</option>
                    <option value="other">{t(lang, 'identity_type_other')}</option>
                  </select>
                </Field>
                <Field label={t(lang, 'identity_number')}><input value={nationalId} onChange={(e) => setNationalId(e.target.value)} className="patient-input" dir="ltr" /></Field>
              </div>
              <Field label={t(lang, 'nationality')}><input value={nationality} onChange={(e) => setNationality(e.target.value)} className="patient-input" /></Field>
              <Field label={t(lang, 'referring_provider')}><input value={sourceProvider} onChange={(e) => setSourceProvider(e.target.value)} className="patient-input read-only:bg-slate-100 read-only:text-slate-500" placeholder={t(lang, 'referring_provider_placeholder')} readOnly={sourceProviderLocked} /></Field>
              <Field label={t(lang, 'payment_method')} required>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as '' | 'cash' | 'insurance')} className="patient-input">
                  <option value="" disabled>{t(lang, 'payment_method_placeholder')}</option>
                  <option value="cash">{t(lang, 'payment_method_cash')}</option>
                  <option value="insurance">{t(lang, 'payment_method_insurance')}</option>
                </select>
              </Field>
            </div>
            <Field label={t(lang, 'referral_reason')} required><textarea value={cause} onChange={(e) => setCause(e.target.value)} className="patient-input min-h-32 resize-y" placeholder={t(lang, 'referral_reason_placeholder')} /></Field>

            <div>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <p className="text-xs font-bold text-slate-600">{t(lang, 'documents_title')}</p>
                <p className="text-[11px] text-slate-400">{t(lang, 'documents_rules')}</p>
              </div>
              <div className="space-y-3">
                {DOC_SECTIONS.map((section) => (
                  <div key={section.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold">{t(lang, section.titleKey)}</p>
                        <p className="mt-0.5 text-[11px] text-slate-400">{t(lang, section.hintKey)}</p>
                      </div>
                      <label className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-xs font-bold transition hover:border-slate-500" style={{ color: accent }}>
                        <UploadCloud className="h-3.5 w-3.5" />
                        {t(lang, section.multiple ? 'add_files' : 'add_file')}
                        <input
                          type="file"
                          multiple={section.multiple}
                          accept="application/pdf,image/jpeg,image/png"
                          className="sr-only"
                          onChange={(e) => { addDocuments(section, Array.from(e.target.files ?? [])); e.target.value = ''; }}
                        />
                      </label>
                    </div>
                    {docs[section.id].length > 0 && (
                      <div className="mt-2.5 space-y-1">
                        {docs[section.id].map((file, index) => (
                          <div key={`${file.name}-${file.size}-${index}`} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <span className="min-w-0 flex-1 truncate">{file.name}</span>
                            <span dir="ltr" className="shrink-0 text-slate-400">{formatDocumentSize(file.size)}</span>
                            <button type="button" onClick={() => removeDocument(section.id, index)} aria-label={t(lang, 'remove_file')} title={t(lang, 'remove_file')} className="shrink-0 text-slate-400 transition hover:text-red-600">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4" />
              <span className="text-slate-600">{isAr ? t(lang, 'consent_text_ar') : info.consent_text}</span>
            </label>
            {error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{formErrorText(lang, error)}</p>}
            <button onClick={submit} disabled={!ready} className="w-full rounded-xl py-3.5 text-sm font-black text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40" style={{ backgroundColor: accent }}>
              {submitting ? t(lang, totalFiles > 0 ? 'submitting_with_uploads' : 'submitting') : t(lang, 'submit')}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400"><LockKeyhole className="h-3 w-3" /> {t(lang, 'secure_footer')}</p>
          </div>
        </div>
      </main>
      <style>{`.patient-input{width:100%;border:1px solid #d8d8d4;border-radius:.7rem;padding:.68rem .8rem;font-size:.875rem;background:#fff;outline:none}.patient-input:focus{border-color:${accent};box-shadow:0 0 0 3px ${accent}16}`}</style>
    </div>
  );
}

async function executeRecaptcha(siteKey: string): Promise<string> {
  type Recaptcha = { ready: (cb: () => void) => void; execute: (key: string, options: { action: string }) => Promise<string> };
  const w = window as typeof window & { grecaptcha?: Recaptcha };
  if (!w.grecaptcha) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true; script.onload = () => resolve(); script.onerror = () => reject(new Error(RECAPTCHA_FAILED_SENTINEL));
      document.head.appendChild(script);
    });
  }
  return new Promise<string>((resolve, reject) => {
    w.grecaptcha?.ready(() => w.grecaptcha!.execute(siteKey, { action: 'direct_intake' }).then(resolve).catch(reject));
  });
}

function Done({ lang, reference, hospitalId, uploaded, uploadErrors }: { lang: Lang; reference: string; hospitalId: string; uploaded: number; uploadErrors: number }) {
  const [status, setStatus] = useState('received');
  const [checking, setChecking] = useState(false);
  const isAr = lang === 'ar';
  const check = async () => {
    setChecking(true);
    try {
      const response = await fetch(`/api/intake/status/${hospitalId}/${reference}`);
      if (response.ok) setStatus((await response.json()).stage);
    } finally {
      setChecking(false);
    }
  };
  const steps: { id: string; labelKey: IntakeStringKey }[] = status === 'declined'
    ? [
        { id: 'received', labelKey: 'stage_received' },
        { id: 'in_review', labelKey: 'stage_in_review' },
        { id: 'declined', labelKey: 'stage_declined' },
      ]
    : [
        { id: 'received', labelKey: 'stage_received' },
        { id: 'in_review', labelKey: 'stage_in_review' },
        { id: 'approved', labelKey: 'stage_approved' },
      ];
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === status));
  return (
    <Centered>
      <div className="w-full max-w-md rounded-3xl border border-stone-200 bg-white p-7 text-center shadow-xl shadow-slate-900/5">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100"><Check className="h-6 w-6 text-emerald-700" /></div>
        <h1 className="mt-4 text-xl font-black">{t(lang, 'done_title')}</h1>
        <p className="mt-1 text-sm text-stone-500">{t(lang, 'done_keep_reference')}</p>
        <p dir="ltr" className="my-5 break-all rounded-xl bg-stone-100 px-3 py-3 text-center font-mono text-sm font-black tracking-wide">{reference}</p>
        {uploaded > 0 && <p className="text-xs text-emerald-700">{uploaded === 1 ? t(lang, 'done_uploaded_one') : t(lang, 'done_uploaded_many', { count: uploaded })}</p>}
        {uploadErrors > 0 && <p className="mt-1 text-xs text-amber-700">{uploadErrors === 1 ? t(lang, 'done_upload_errors_one') : t(lang, 'done_upload_errors_many', { count: uploadErrors })}</p>}
        <div className="mt-6 grid grid-cols-3 gap-1">
          {steps.map((step, index) => {
            const reached = index <= currentIndex;
            const terminalDecline = step.id === 'declined' && reached;
            return (
              <div key={step.id} className="relative pt-5">
                {index > 0 && <span className={`absolute top-2.5 h-px ${isAr ? 'left-1/2 right-[-52%]' : 'left-[-52%] right-1/2'} ${reached ? (terminalDecline ? 'bg-amber-400' : 'bg-emerald-500') : 'bg-stone-200'}`} />}
                <span className={`absolute left-1/2 top-0 z-10 h-5 w-5 -translate-x-1/2 rounded-full border-4 border-white ${reached ? (terminalDecline ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-stone-200'}`} />
                <p className={`text-[11px] font-bold ${reached ? 'text-stone-800' : 'text-stone-400'}`}>{t(lang, step.labelKey)}</p>
              </div>
            );
          })}
        </div>
        <button onClick={check} disabled={checking} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-indigo-800 hover:underline disabled:opacity-60">
          <RefreshCw className={`h-3.5 w-3.5 ${checking ? 'animate-spin' : ''}`} />
          {checking ? t(lang, 'checking_status') : t(lang, 'refresh_status')}
        </button>
      </div>
    </Centered>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) { return <label className="block"><span className="mb-1.5 block text-xs font-bold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</span>{children}</label>; }
function Centered({ children }: { children: React.ReactNode }) { return <div className="flex min-h-dvh items-center justify-center bg-[#f4f2ed] p-5">{children}</div>; }
function MessageCard({ title, message }: { title: string; message: string }) { return <div className="w-full max-w-md rounded-2xl border border-stone-200 bg-white p-6 text-center"><h1 className="text-lg font-black text-red-700">{title}</h1><p className="mt-2 text-sm text-stone-600">{message}</p></div>; }
function Spinner() { return <div className="h-8 w-8 animate-spin rounded-full border-2 border-stone-300 border-t-indigo-800" />; }
